import { ForwardedRef, forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Position } from 'types'
import { Renderable } from '../classes/renderer'
import Shader from '../classes/shader'
import { ArtboardContext } from '../context'
import { useText } from '../hooks/use-text'
import { ArtboardTextLayerProps } from '../types'
import ArtboardLayer from './base'

const ArtboardText = (
    {
        align = 'center',
        color = '#fff',
        fontFamily = 'interface',
        fontSize = 24,
        fontWeight = 'normal',
        mirrored = false,
        text,
        strokeColor = '#000',
        strokeSize = 0,
        width,
        onPositionUpdate: onPositionUpdateProp,
        ...rest
    }: ArtboardTextLayerProps,
    ref: ForwardedRef<Renderable>
) => {
    const textureRef = useRef<WebGLTexture | null>(null)
    const { renderer } = useContext(ArtboardContext)
    const [scale, setScale] = useState<number>(1)
    const [canvas, time] = useText(
        text,
        width,
        color,
        fontSize,
        fontFamily,
        fontWeight,
        align,
        strokeColor,
        strokeSize,
        scale
    )
    const height = useMemo(() => (canvas ? Math.round(width / (canvas.width / canvas.height)) : 0), [time])

    function onDraw(position: Position, shader: Shader) {
        if (!textureRef.current || !shader) return

        shader.drawImage({ ...position, mirrored, texture: textureRef.current })
    }

    function onPositionUpdate(position: Position, isDragging: boolean) {
        if (!isDragging) {
            setScale(Math.max(1, position.width / width))
        }

        onPositionUpdateProp?.(position, isDragging)
    }

    useEffect(() => {
        if (canvas && renderer) {
            textureRef.current = renderer.createTexture(canvas)
        }
    }, [renderer, time])

    return (
        <ArtboardLayer
            {...rest}
            height={height}
            ref={ref}
            width={width}
            onDraw={onDraw}
            onPositionUpdate={onPositionUpdate}
        />
    )
}

export default forwardRef(ArtboardText)
